/**
 * handlers.planning.ts — offline replay for the PLANNING writes: the daily plan's
 * stop ordering, the weekly plan's mission→day assignment, and the rep's
 * mission-wishes preferences.
 *
 * Charter: ONE bounded context — what a rep writes when PLANNING a day or a week
 * (as opposed to executing one, which is `./handlers.missionDay`). Registered by
 * the `./handlers` facade.
 */

import { registerQueueHandler } from './offlineQueue'
import { reorderMissionLead, updateMission } from '../api/missions'
import { updateRepPreferences, updateRepPreferencesWeights } from '../api/repPreferences'
import type { RepPreferencesWeights, RepWeightLiteral } from '../api/repPreferences'
import { updateUserPlzAreas } from '../api/users'
import { QUEUE_TYPES } from './queueTypes'

/**
 * Mission-lead REORDER — the daily-plan stop ordering write (move up/down, drag
 * to index, anchor reorder). Bypassed the durable path entirely: a transient
 * 502 on the border network dropped the reorder with only a toast. Queued now so
 * the ordering the rep set survives a reconnect (BUG-FE-PLANNING-WRITE-BYPASS).
 */
export interface MissionLeadReorderPayload {
  missionId: string
  missionLeadId: string
  newIndex: number
}

/**
 * Mission DATE assignment — the Wochenplanung drag/assign that pins a mission to
 * a weekday (persistMissionDay / assignMissionToDay). Went through a bare
 * react-query mutation with no outbox, so an offline assign was lost. Queued now
 * so the week the rep planned replays.
 */
export interface MissionDateUpdatePayload {
  missionId: string
  date: string | null
}

/**
 * Rep MISSION-WISHES save — the THREE-write preferences flow (rep_preferences
 * upsert + users.plz_areas + tristate weights). Written sequentially with no
 * transaction, a mid-sequence failure left a PARTIAL write (e.g. focus_plz
 * committed, weights lost). Queued as ONE outbox entry so replay re-applies all
 * three atomically (each write is an idempotent upsert).
 */
export interface RepPreferencesSavePayload {
  email: string
  focus_plz: string[]
  focus_products: string[]
  focus_industries: string[]
  product_weights: Record<string, RepWeightLiteral>
  industry_weights: Record<string, RepWeightLiteral>
}

export function registerPlanningHandlers(): void {
  // Daily-plan stop reorder — replay the ordering the rep set (move/drag/anchor).
  registerQueueHandler(QUEUE_TYPES.MISSION_LEAD_REORDER, async (payload) => {
    const p = payload as MissionLeadReorderPayload
    await reorderMissionLead(p.missionId, p.missionLeadId, p.newIndex)
  })

  // Weekly-plan mission→day assignment — replay the date pin.
  registerQueueHandler(QUEUE_TYPES.MISSION_DATE_UPDATE, async (payload) => {
    const p = payload as MissionDateUpdatePayload
    await updateMission(p.missionId, { date: p.date })
  })

  // Rep mission-wishes — replay ALL THREE writes atomically so a queued save
  // never lands as a partial. Each is an idempotent upsert, so re-applying a
  // leg that already committed before the original failure is safe.
  registerQueueHandler(QUEUE_TYPES.REP_PREFERENCES_SAVE, async (payload) => {
    const p = payload as RepPreferencesSavePayload
    await updateRepPreferences(p.email, {
      focus_plz: p.focus_plz,
      focus_products: p.focus_products,
      focus_industries: p.focus_industries,
    })
    await updateUserPlzAreas(p.email, p.focus_plz)
    const weights: RepPreferencesWeights = {
      product_weights: p.product_weights,
      industry_weights: p.industry_weights,
    }
    await updateRepPreferencesWeights(p.email, weights)
  })
}
